import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

import { Button } from '@/components/ui/button';
import { Display, Heading, Text } from '@/components/ui/typography';
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { supabaseBrowser as supabase } from '@/lib/supabase-browser';
import { useSession } from '@/hooks/useSession';
import { toast } from '@/hooks/use-toast';
import { track } from '@/lib/analytics/track';
import { cn } from '@/lib/utils';
import { MOTION } from '@/lib/motion';
import { getCurrentSession } from '@/lib/rpc/auth-facade';
import { useSessionGuard } from '@/hooks/useSessionGuard';
import { saveDraft, loadDraft, clearDraft } from '@/lib/onboarding/draftState';
import { getOnboarding } from '@/lib/rpc/user/onboarding';
import { savePendingKycLink } from '@/lib/onboarding/pendingKycState';
import { semanticTokens } from '@packages/config/theme';

const profileSchema = z.object({
  type: z.enum(['individual', 'business'], { required_error: 'Select account type' }),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  businessName: z.string().optional(),
  // Email removed - will use session.user.email directly
}).refine(
  (data) => {
    if (data.type === 'individual') {
      const hasFirstName = data.firstName && data.firstName.trim().length >= 1;
      const hasLastName = data.lastName && data.lastName.trim().length >= 1;
      return hasFirstName && hasLastName;
    }
    return data.businessName && data.businessName.trim().length >= 2;
  },
  (data) => {
    if (data.type === 'individual') {
      if (!data.firstName || data.firstName.trim().length < 1) {
        return { message: 'Enter first name', path: ['firstName'] };
      }
      if (!data.lastName || data.lastName.trim().length < 1) {
        return { message: 'Enter last name', path: ['lastName'] };
      }
    } else {
      if (!data.businessName || data.businessName.trim().length < 2) {
        return { message: 'Enter company name (minimum 2 characters)', path: ['businessName'] };
      }
    }
    return { message: 'Fill in all required fields', path: ['firstName'] };
  }
);

type ProfileFormValues = z.infer<typeof profileSchema>;

interface ParsedError {
  message: string;
  code: string;
  status: number;
  retryAfter?: number | null;
}

function parseError(error: unknown): ParsedError {
  const fallback: ParsedError = {
    message: 'Failed to create identity verification link. Please try again later.',
    code: 'kyc_link_failed',
    status: 500,
    retryAfter: null,
  };

  if (!error || typeof error !== 'object') {
    return fallback;
  }

  const record = error as Record<string, unknown>;
  const status = typeof record.status === 'number' ? record.status : fallback.status;
  let message = typeof record.message === 'string' ? record.message : fallback.message;
  let code = typeof record.name === 'string' ? record.name : fallback.code;
  let retryAfter: number | null = null;

  const context = record.context && typeof record.context === 'object' ? (record.context as Record<string, unknown>) : null;
  const details = record.details && typeof record.details === 'object' ? (record.details as Record<string, unknown>) : null;

  if (context) {
    if (typeof context.code === 'string') {
      code = context.code;
    }
    if (typeof context.message === 'string') {
      message = context.message;
    }
    if (typeof context.retry_after === 'number') {
      retryAfter = context.retry_after;
    }
  }

  if (!retryAfter && details && typeof details.retry_after === 'number') {
    retryAfter = details.retry_after;
  }

  const messageText = typeof record.message === 'string' ? record.message : null;
  if (messageText) {
    try {
      const parsed = JSON.parse(messageText) as Record<string, unknown>;
      const parsedError = parsed.error && typeof parsed.error === 'object' ? (parsed.error as Record<string, unknown>) : null;
      if (parsedError) {
        if (typeof parsedError.message === 'string') {
          message = parsedError.message;
        }
        if (typeof parsedError.code === 'string') {
          code = parsedError.code;
        }
      }
      if (typeof parsed.retry_after === 'number') {
        retryAfter = parsed.retry_after;
      }
      if (typeof parsed.status === 'number') {
        return { message, code, status: parsed.status, retryAfter };
      }
    } catch {
      // ignore JSON parse errors
    }
  }

  return { message, code, status, retryAfter };
}

function getDefaultRedirect(): string {
  if (typeof window !== 'undefined' && window.location) {
    try {
      return new URL('/verify', window.location.origin).toString();
    } catch {
      return 'https://www.supabank.xyz/verify';
    }
  }
  return 'https://www.supabank.xyz/verify';
}

export default function OnboardingProfile() {
  const navigate = useNavigate();
  const session = useSession();
  const { sessionState, expiresIn, refreshSession } = useSessionGuard();
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [retryAfter, setRetryAfter] = useState<number | null>(null);
  const isRateLimited = retryAfter != null && retryAfter > 0;

  const userEmail = useMemo(() => {
    return session.status === 'authed' ? session.user?.email ?? null : null;
  }, [session.status, session.user?.email]);

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      type: 'individual',
      firstName: '',
      lastName: '',
      businessName: '',
    },
    mode: 'onChange',
  });

  // Check for returning users (profile already completed)
  useEffect(() => {
    const checkReturningUser = async () => {
      try {
        const onboardingStatus = await getOnboarding();

        // If user has completed profile, redirect to resume page
        if (onboardingStatus.flags.profile_completed) {
          track('onboarding_returning_user_redirect', {
            account_type: onboardingStatus.account_type,
            state: onboardingStatus.state,
          });
          navigate('/onboarding/resume', { replace: true });
        }
      } catch (error) {
        console.error('[OnboardingProfile] Failed to check returning user status:', error);
        // Don't block the form if check fails - let user continue
      }
    };

    void checkReturningUser();
  }, [navigate]);

  // Load draft on mount
  useEffect(() => {
    const draft = loadDraft();
    if (draft) {
      form.setValue('type', draft.type);
      if (draft.firstName) form.setValue('firstName', draft.firstName);
      if (draft.lastName) form.setValue('lastName', draft.lastName);
      if (draft.businessName) form.setValue('businessName', draft.businessName);

      toast({
        title: 'Draft Restored',
        description: 'Continue filling out the form from where you left off.',
      });
    }
  }, [form]);

  // Auto-save draft on form changes (debounced)
  useEffect(() => {
    const subscription = form.watch((values) => {
      saveDraft({
        type: values.type,
        firstName: values.firstName,
        lastName: values.lastName,
        businessName: values.businessName,
      });
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const handleSuccess = useCallback((kycLink: string) => {
    track('kyc_profile_link_open', { surface: 'profile-form' });
    if (typeof window !== 'undefined') {
      window.open(kycLink, '_blank', 'noopener');
    }
    toast({
      title: 'Identity Verification Opened',
      description: 'If the window didn\'t appear, open the link manually.',
    });
  }, []);

  const onSubmit = useCallback(async (values: ProfileFormValues) => {
    if (submitting || isRateLimited) {
      return;
    }

    // Check session validity before submission
    if (sessionState === 'expired') {
      setServerError('Session expired. Please sign in again to continue.');
      track('kyc_profile_submit_failed', { code: 'session_expired' });
      return;
    }

    // Use auth email directly (no form email field)
    if (!userEmail) {
      setServerError('Email not found in session. Please sign in again.');
      track('kyc_profile_submit_failed', { code: 'missing_email' });
      return;
    }

    setSubmitting(true);
    setServerError(null);
    setRetryAfter(null);

    try {
      const redirectUri = getDefaultRedirect();
      const body: Record<string, unknown> = {
        action: 'start',  // Hub routing: action=start
        type: values.type,
        email: userEmail, // Always use auth email
        redirect_uri: redirectUri,
        endorsements: ['base'],
        force_new: false,
      };

      // Include name fields based on account type
      if (values.type === 'individual') {
        body.first_name = values.firstName;
        body.last_name = values.lastName;
      } else {
        body.business_name = values.businessName;
      }

      const currentSession = await getCurrentSession();

      if (!currentSession?.access_token) {
        setServerError('Session expired. Please sign in again to continue.');
        track('kyc_profile_submit_failed', { code: 'unauthorized', status: 401 });
        return;
      }

      // NEW: Save profile completion to database (for returning user flow)
      const { data: profileData, error: profileError } = await supabase.rpc('save_profile_completion', {
        p_account_type: values.type,
        p_first_name: values.type === 'individual' ? values.firstName : null,
        p_last_name: values.type === 'individual' ? values.lastName : null,
        p_business_name: values.type === 'business' ? values.businessName : null,
      });

      if (profileError || (profileData && !(profileData as { success: boolean }).success)) {
        console.error('[OnboardingProfile] Failed to save profile:', profileError || profileData);
        // Don't block KYC flow - continue even if profile save fails
        track('profile_save_failed', { error: profileError?.message || 'unknown' });
      } else {
        track('profile_saved', { account_type: values.type });
      }

      // Call unified KYC Hub using direct fetch (CRITICAL: ensure headers are passed correctly)
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (!supabaseUrl) {
        setServerError('Configuration error. Please refresh the page.');
        return;
      }

      // eslint-disable-next-line internal/no-direct-edge-fetch -- Special case: KYC start endpoint
      const response = await fetch(`${supabaseUrl}/functions/v1/kyc`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${currentSession.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY || ''
        },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData: any;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { message: errorText };
        }
        
        const parsed = {
          status: response.status,
          code: errorData?.error?.code || 'unknown_error',
          message: errorData?.error?.message || errorData?.message || 'KYC start failed'
        };
        if (parsed.status === 401) {
          setServerError('Session expired. Please sign in again to continue.');
          return;
        }
        if (parsed.status === 429) {
          const retry = parsed.retryAfter && parsed.retryAfter > 0 ? parsed.retryAfter : 30;
          setRetryAfter(retry);
          setServerError(`Provider temporarily unavailable. Please try again in ${retry} sec.`);
          track('kyc_profile_rate_limited', { retryAfter: retry });
          return;
        }
        setServerError(parsed.message);
        track('kyc_profile_submit_failed', { code: parsed.code, status: parsed.status });
        return;
      }

      const data = await response.json();

      const payload = data && typeof data === 'object' ? (data as Record<string, unknown>) : {};

      // Handle nested response: { data: { link: { kyc_link: ... } } }
      const innerData = payload.data && typeof payload.data === 'object'
        ? (payload.data as Record<string, unknown>)
        : payload;

      const linkRecord = innerData.link && typeof innerData.link === 'object'
        ? (innerData.link as Record<string, unknown>)
        : innerData;

      const kycLink = typeof linkRecord.kyc_link === 'string'
        ? linkRecord.kyc_link
        : typeof linkRecord.url === 'string'
          ? linkRecord.url
          : null;

      const tosLink = typeof linkRecord.tos_link === 'string' ? linkRecord.tos_link : null;
      const tosStatus = typeof linkRecord.tos_status === 'string' ? linkRecord.tos_status : 'pending';

      if (!kycLink) {
        console.error('[OnboardingProfile] Failed to extract kyc_link from response:', { payload, innerData, linkRecord });
        setServerError('Failed to get identity verification link. Please try again.');
        track('kyc_profile_submit_failed', { code: 'missing_link' });
        return;
      }

      track('kyc_profile_submitted', { type: values.type });

      // Clear draft after successful submission
      clearDraft();

      // PRD-05-PHASE-3: ToS Auto-Resume Flow with Polling
      if (tosLink && tosStatus !== 'approved') {
        // Save KYC link and ToS link to localStorage (survives page reloads and navigation)
        savePendingKycLink(kycLink);
        localStorage.setItem('supabank:pending_tos_link', tosLink);

        track('tos_gate_redirect', { tosStatus, hasTosLink: Boolean(tosLink) });

        // Redirect to ToS waiting page (will auto-open ToS in new window + poll for acceptance)
        navigate('/onboarding/tos-waiting', { replace: true });
        return;
      }

      // ToS already approved or not required → open KYC link directly
      handleSuccess(kycLink);
      navigate('/onboarding/wizard', { replace: false });
    } catch (err) {
      console.error('[OnboardingProfile] submit error', err);
      setServerError('An unexpected error occurred. Please try again later.');
      track('kyc_profile_submit_failed', { code: 'unexpected', message: err instanceof Error ? err.message : String(err) });
    } finally {
      setSubmitting(false);
    }
  }, [handleSuccess, isRateLimited, navigate, sessionState, submitting, userEmail]);

  const retryCountdown = useMemo(() => {
    if (retryAfter == null) {
      return null;
    }
    if (retryAfter <= 0) {
      return null;
    }
    return retryAfter;
  }, [retryAfter]);

  useEffect(() => {
    if (retryAfter == null) {
      return;
    }
    if (retryAfter <= 0) {
      return;
    }
    const timer = window.setInterval(() => {
      setRetryAfter(prev => {
        if (prev == null) {
          return null;
        }
        return prev > 0 ? prev - 1 : 0;
      });
    }, 1000);
    return () => window.clearInterval(timer);
  }, [retryAfter]);

  return (
    <motion.div 
      className={cn(
        "mx-auto flex w-full max-w-2xl flex-col gap-8",
        // Mobile: more horizontal padding, safe area
        "px-4 py-6 pb-24 sm:px-6 sm:py-10",
        semanticTokens.bg.canvas
      )}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: MOTION.dur.standard, ease: MOTION.ease.standard }}
    >
      <div className="space-y-2">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className={cn(
            "text-sm min-h-[44px] px-2 py-2 -ml-2",
            semanticTokens.fg.muted,
            semanticTokens.motion.standard,
            "hover:text-foreground",
            semanticTokens.focus.ring,
            "rounded-md"
          )}
        >
          ← Back
        </button>
        <Heading level={1}>Complete Profile Before Verification</Heading>
        <Text variant="sm" color="muted">
          We need basic information about you or your company to start identity verification with Bridge. This data will be shared with the provider and saved in Supabank.
        </Text>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem className="space-y-3">
                <FormLabel className={semanticTokens.typography.label}>Who is being verified?</FormLabel>
                <FormControl>
                  <RadioGroup
                    onValueChange={field.onChange}
                    value={field.value}
                    className="grid gap-3 sm:grid-cols-2"
                  >
                    <label
                      className={cn(
                        'flex cursor-pointer flex-col p-4 text-left',
                        // Touch target
                        'min-h-[72px]',
                        // Border and radius
                        semanticTokens.radius.lg,
                        semanticTokens.border.default,
                        'border',
                        // Transitions
                        semanticTokens.motion.standard,
                        // Focus ring
                        'focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2',
                        // Active state
                        field.value === 'individual' 
                          ? cn(semanticTokens.border.focus, 'bg-primary/5 ring-2 ring-primary ring-offset-2') 
                          : ''
                      )}
                    >
                      <RadioGroupItem value="individual" className="sr-only" />
                      <span className={cn("font-medium", semanticTokens.fg.default)}>Individual</span>
                      <span className={cn("text-sm", semanticTokens.fg.muted)}>Verification for individual customer</span>
                    </label>
                    <label
                      className={cn(
                        'flex cursor-pointer flex-col p-4 text-left',
                        // Touch target
                        'min-h-[72px]',
                        // Border and radius
                        semanticTokens.radius.lg,
                        semanticTokens.border.default,
                        'border',
                        // Transitions
                        semanticTokens.motion.standard,
                        // Focus ring
                        'focus-within:ring-2 focus-within:ring-blue-500 focus-within:ring-offset-2',
                        // Active state
                        field.value === 'business' 
                          ? cn(semanticTokens.border.focus, 'bg-primary/5 ring-2 ring-primary ring-offset-2') 
                          : ''
                      )}
                    >
                      <RadioGroupItem value="business" className="sr-only" />
                      <span className={cn("font-medium", semanticTokens.fg.default)}>Company</span>
                      <span className={cn("text-sm", semanticTokens.fg.muted)}>Verification for business entity</span>
                    </label>
                  </RadioGroup>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {form.watch('type') === 'individual' ? (
            <>
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>First Name</FormLabel>
                    <FormControl>
                      <Input 
                        id="onboarding-firstname" 
                        placeholder="John" 
                        autoComplete="given-name"
                        autoCapitalize="words"
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Last Name</FormLabel>
                    <FormControl>
                      <Input 
                        id="onboarding-lastname" 
                        placeholder="Smith" 
                        autoComplete="family-name"
                        autoCapitalize="words"
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </>
          ) : (
            <FormField
              control={form.control}
              name="businessName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Legal Company Name</FormLabel>
                  <FormControl>
                    <Input 
                      id="onboarding-businessname" 
                      placeholder="Acme Corporation Inc." 
                      autoComplete="organization"
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {/* Read-only email display (from auth session) */}
          {userEmail ? (
            <div className="space-y-2">
              <label className="text-sm font-medium">Email for Notifications</label>
              <div className="rounded-md border border-border bg-muted/20 px-3 py-2 text-sm text-muted-foreground">
                {userEmail}
              </div>
              <p className="text-xs text-muted-foreground">Email is taken from your account and cannot be changed</p>
            </div>
          ) : null}

          {/* Session expiration warning */}
          {sessionState === 'expiring' && expiresIn ? (
            <div className="rounded-md border border-warning/40 bg-warning/10 p-3 text-sm">
              <div className="flex items-start gap-3">
                <SFIcon icon={sfIcons.warning} className="h-4 w-4 text-warning flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <p className="font-medium text-warning">Session Expiring Soon</p>
                  <p className="text-warning">
                    {Math.floor(expiresIn / 60)} min {expiresIn % 60} sec remaining. Your data has been saved automatically.
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => refreshSession()}
                    className="border-warning text-warning hover:bg-warning/50/20"
                  >
                    Refresh Session
                  </Button>
                </div>
              </div>
            </div>
          ) : null}

          {serverError ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
              {serverError}
              {retryCountdown ? <div className="mt-1 text-xs text-muted-foreground">Retry available in {retryCountdown} sec.</div> : null}
            </div>
          ) : null}

          {/* Single smart CTA button */}
          <Button 
            type="submit" 
            disabled={submitting || isRateLimited || sessionState === 'expired'} 
            className={cn(
              "w-full sm:w-auto",
              "min-h-[48px]",
              semanticTokens.motion.standard,
              semanticTokens.focus.ring,
              // Button press feedback
              "active:scale-[0.98] transition-transform"
            )}
            aria-busy={submitting}
          >
            {submitting ? 'Saving…' : isRateLimited ? 'Please Wait…' : sessionState === 'expired' ? 'Session Expired' : 'Continue'}
          </Button>

          {!submitting && !isRateLimited && sessionState !== 'expired' ? (
            <p className="text-xs text-muted-foreground">
              By clicking "Continue", you'll save your data and open identity verification with Bridge
            </p>
          ) : null}
        </form>
      </Form>

      <div className="rounded-lg border border-border bg-muted/20 p-4">
        <Text variant="sm" color="default" className="font-medium">What happens next?</Text>
        <ol className="mt-2 list-decimal space-y-2 pl-5">
          <li>We'll save the entered data to your Supabank profile.</li>
          <li>A secure Bridge form will open for document upload and verification.</li>
          <li>After verification is complete, you'll be returned to the status page.</li>
        </ol>
      </div>
    </motion.div>
  );
}
